import React, { useRef } from 'react'
import {
  SubmitHandler,
  useForm,
  Controller,
  FormProvider,
} from 'react-hook-form'
import { Task } from '@core/entities/task/Task'
import { useModal } from '@common/GlobalModal'
import { useToast } from '@core/toast'
import { TaskAssignableUser } from '@core/entities/task/TaskAssignableUser'
import { useSubmitTaskStep } from '@core/react-query/features/tasks/task'
import { useNavigationContext } from '@common/hooks'
import {
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
} from '@common/material'
import { Flex } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import ClearIcon from '@mui/icons-material/Clear'
import { TaskAssignUserPicker } from '@elements'
import {
  QuestionsForm,
  VirtualizedQuestionGroupListHandle,
} from '@modules/template-form'
import { validateQuestionsStage } from '@elements/Questions/validation/questionsStageGate'
import { TaskTemplate } from '@core/entities/task/TaskTemplate'
import { TemplateAnswersFormValues } from '@core/components/templateAnswersForm'
import { localizedStrings } from '@core/strings'

interface ComponentType {
  task: Task
  isAddressChanges?: boolean
}

export type NextTaskStepFormFields = TemplateAnswersFormValues<TaskTemplate> & {
  assigneeUser: TaskAssignableUser
}

export const NextTaskStepModal: React.FC<ComponentType> = ({
  task,
  isAddressChanges = false,
}) => {
  const { organisationId, projectId } = useNavigationContext()
  const toast = useToast()
  const { hideModal } = useModal()
  const submitTaskStepMutation = useSubmitTaskStep()
  const questionsFormRef = useRef<VirtualizedQuestionGroupListHandle>(null)
  const taskNextStepQuestions = task?.getCurrentStepQuestions()?.toPlain?.()
  const taskNextStepQuestionsAnswers = task
    ?.getCurrentStepQuestionsAnswers()
    ?.toPlain?.()

  const formContext = useForm<NextTaskStepFormFields>({
    defaultValues: {
      projectId: task?.project?.id,
      projectLocations: task?.projectLocations,
      assigneeUser: isAddressChanges
        ? TaskAssignableUser.new(task?.lastRequestedChange?.requestedBy)
        : TaskAssignableUser.new(task?.assignee),
      // If the step is answered, use the answers, otherwise use the questions
      questions: task?.hasCurrentStepQuestionsAnswers()
        ? taskNextStepQuestionsAnswers?.answers
        : taskNextStepQuestions?.questions,
      rules: taskNextStepQuestions?.rules,
    },
  })

  const onSubmit: SubmitHandler<NextTaskStepFormFields> = (data) => {
    const canSubmit = validateQuestionsStage({
      formContext,
      questionsFormKey: 'questions',
      scrollToQuestionGroup: (groupIndex) => {
        questionsFormRef.current?.scrollToQuestionGroup(groupIndex)
      },
    })
    if (!canSubmit) {
      return
    }

    submitTaskStepMutation.mutate(
      {
        id: task.id,
        taskTemplateId: task.template.currentVersion.templateId,
        assigneeUserId: data.assigneeUser.id,
        stepAnswers: { answers: data.questions },
      },
      {
        onSuccess: () => {
          toast.success('Task moved to next step successfully')
        },
        onError: (error) => {
          toast.error(error.message)
        },
        onSettled: () => {
          hideModal()
        },
      },
    )
  }

  return (
    <FormProvider {...formContext}>
      <ModalDialogContainer>
        <DialogTitle
          variant='h5'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          sx={{ pr: 1, fontSize: 20 }}
        >
          {`Move to ${task.isNextStepDone() ? 'Done' : task.nextStep?.name}`}
          <IconButton aria-label='close-modal' onClick={hideModal}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Flex flexDirection={'column'} sx={{ mt: 2 }}>
            <form onSubmit={formContext.handleSubmit(onSubmit)}>
              {!task.isNextStepDone() && (
                <Controller
                  control={formContext.control}
                  name='assigneeUser'
                  rules={{ required: 'Please select an assignee' }}
                  render={({ field, fieldState }) => (
                    <TaskAssignUserPicker
                      projectId={projectId ?? task?.project?.id}
                      templateId={task.template.currentVersion.templateId}
                      stepId={task?.nextStep?.id}
                      value={field.value}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                    />
                  )}
                />
              )}

              {task.hasCurrentStepQuestions() && (
                <Flex
                  flexDirection='column'
                  gap={2}
                  mt={2}
                  data-test='next-task-step-questions'
                >
                  <QuestionsForm
                    ref={questionsFormRef}
                    organisationId={organisationId}
                    questionsFormKey='questions'
                  />
                </Flex>
              )}

              <Flex gap={2} mt={4}>
                <Button variant='outlined' onClick={hideModal} fullWidth>
                  {localizedStrings.cancel}
                </Button>
                <Button
                  variant='contained'
                  type='submit'
                  fullWidth
                  data-test='save-task'
                  disabled={submitTaskStepMutation.isLoading}
                >
                  {localizedStrings.save}
                </Button>
              </Flex>
            </form>
          </Flex>
        </DialogContent>
      </ModalDialogContainer>
    </FormProvider>
  )
}
